/**
 * SearchResultsPanel - 図書館画面の「検索結果」タブの中身
 *
 * 並び替え・図書館フィルタ・全館検索のツールバーは検索結果にだけ効くため、
 * タブの上ではなくこのパネルの先頭に置く (スワイプ中に上部の高さが変わらないようにする)。
 */

import { ArrowUpDown, Filter } from 'lucide-react'
import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useLibraryFilterStore } from '@/store/useLibraryFilterStore'
import { useLibrarySearchStore } from '@/store/useLibrarySearchStore'
import { isDefaultSort, useLibrarySortStore } from '@/store/useLibrarySortStore'

import { sortBooks } from '../lib/sort-books'
import { BookList } from './BookList'

type Props = {
  expandedBookIds: ReadonlySet<string>
  onToggle: (bookId: string) => void
  onOpenSort: () => void
  onOpenFilter: () => void
}

export const SearchResultsPanel = ({ expandedBookIds, onToggle, onOpenSort, onOpenFilter }: Props) => {
  const status = useLibrarySearchStore((s) => s.status)
  const books = useLibrarySearchStore((s) => s.books)
  const error = useLibrarySearchStore((s) => s.error)

  const filterCount = useLibraryFilterStore((s) => s.selectedLibraryIds.length)
  const searchAllLibraries = useLibraryFilterStore((s) => s.searchAllLibraries)
  const setSearchAllLibraries = useLibraryFilterStore((s) => s.setSearchAllLibraries)
  const sortKey = useLibrarySortStore((s) => s.sortKey)
  const sortOrder = useLibrarySortStore((s) => s.sortOrder)
  const sortActive = !isDefaultSort(sortKey, sortOrder)

  const sortedBooks = useMemo(() => sortBooks(books, sortKey, sortOrder), [books, sortKey, sortOrder])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {filterCount > 0 ? (
            <label className="flex cursor-pointer items-center gap-1.5">
              <Switch checked={searchAllLibraries} onCheckedChange={setSearchAllLibraries} />
              すべての図書館で検索
            </label>
          ) : (
            'フィルタなし'
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={sortActive ? 'default' : 'outline'}
            size="sm"
            onClick={onOpenSort}
            className="gap-1"
          >
            <ArrowUpDown className="size-3.5" />
            並び替え
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onOpenFilter}
            aria-label="図書館フィルタ"
            title="図書館フィルタ"
            className="gap-1"
          >
            <Filter className="size-3.5" />
            {filterCount > 0 && (
              <span className={cn('text-xs', searchAllLibraries && 'line-through opacity-60')}>{filterCount}</span>
            )}
          </Button>
        </div>
      </div>

      {status === 'error' && error && (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">{error}</div>
      )}

      {(status === 'loading' || status === 'polling') && <div className="text-sm text-muted-foreground">検索中…</div>}

      {status === 'done' && books.length === 0 && (
        <div className="text-sm text-muted-foreground">該当する本が見つかりませんでした。</div>
      )}

      <BookList books={sortedBooks} expandedBookIds={expandedBookIds} onToggle={onToggle} mode="search" />
    </div>
  )
}
